import { matchPath } from 'react-router-dom';
import kebabCase from 'lodash/kebabCase';
import { ALL_SITES } from '@tempus/trials-types';

import { MatchingTab } from '~/store/timeScreening/constants';

export enum PathEnum {
  ACTIVATION_PACKET = '/trials/:trialId/:siteId/packet',
  ALL_PATIENTS = '/all-patients/:siteId?',
  ADMIN = '/admin',
  BUILD_STATUS = '/build-status',
  BUILDER = '/builder',
  BUILDER_PROJECT = '/builder/projects/:projectId',
  BUILDER_PROJECT_STAGES = '/builder/projects/:projectId/stages',
  BUILDER_RULESET = '/builder/projects/:projectId/rulesets/:rulesetId',
  BUILDER_RULESET_EVALUATION_JOBS = '/builder/projects/:projectId/rulesets/:rulesetId/evaluation-jobs',
  BUILDER_EVALUATION_JOB_DETAIL = '/builder/projects/:projectId/rulesets/:rulesetId/evaluation-jobs/ee/:jobId',
  BUILDER_PATIENT_QUERY_JOB_DETAIL = '/builder/projects/:projectId/rulesets/:rulesetId/evaluation-jobs/pq/:jobId',
  CARE_PATHWAYS = '/care-pathways/:siteId?',
  CARE_PATHWAY_QUEUE = '/care-pathway/:siteId?/:carePathwayId?',
  MATCH_REVIEW = '/match-review/:siteId?',
  PATIENT_TRACKER_SITES = '/patient-tracker',
  PATIENT_TRIAL_ELIGIBILITY_REVIEW = '/eligibility-review/patient/:timePatientId/trial/:trialId',
  PATIENT_V2 = '/patient/:patientLinkageId/:institutionId/:tab?/:carePathwayEvaluationId?',
  /** Legacy eval-only bookmark; redirects to {@link PathEnum.PATIENT_V2} when metadata supplies linkage + site. */
  PATIENT_CARE_PATHWAY = '/next-prescreening/patient-care-pathway/:patientCarePathwayId',
  PROVIDER_REPORTING = '/reporting',
  REPORTING_DASHBOARD = '/reporting/provider-dashboards/:id/resource-type/:resourceType/resource-id/:resourceId',
  SITE_DOCUMENTS = '/sites/:siteId?/documents',
  SITE_PATIENT_TRACKER = '/patient-tracker/sites/:siteId?/:nctId?/:patientFullName?',
  TEMPUS_NURSE = '/tempus-nurse/:siteId?',
  SCREENING_CARE_PATHWAYS = '/screening/care-pathways',
  SCREENING_CARE_PATHWAYS_MATCHING = '/screening/care-pathways/matching/:tab?',
  SCREENING_CARE_PATHWAYS_MATCHING_BASE = '/screening/care-pathways/matching',
  SCREENING_CARE_PATHWAYS_SCREENED = '/screening/care-pathways/screened',
  TIME_SCREENING_TRIALS = '/screening/trials',
  TIME_SCREENING_TRIALS_MATCHING = '/screening/trials/matching/:tab?',
  TIME_SCREENING_TRIALS_MATCHING_BASE = '/screening/trials/matching',
  TIME_SCREENING_TRIALS_SCREENED = '/screening/trials/screened',
  TRIAL_DOCUMENTS = '/trials/:trialId/documents',
  SITE_TRIAL = '/trials/:trialId/site-trial',
  TRIAL_MATERIALS = '/trials/trialMaterials',
  TRIAL_SEARCH = '/trials/trialSearch',
  TRIALS = '/trials/',
  USER_NOTIFICATION_PREFERENCES = '/user/notification-preferences',
  PROGRAM_MANAGEMENT = '/program-management',
  PROGRAM_MANAGEMENT_SITE = '/program-management/sites/:siteId',
  PROGRAM_MANAGEMENT_PATHWAY = '/program-management/pathways/:pathwayId',
}

export enum SubPaths {
  MATCHING = 'matching/:tab?',
  MATERIALS = 'trialMaterials',
  SCREENED = 'screened',
  SEARCH = 'trialSearch',
}

/** Salesforce institution ID regex */
export const INSTITUTION_ID_REGEX = /^(001|a00)[a-zA-Z0-9]{15}$/;

export const checkPath = (url: string, path: string) => {
  return matchPath(path, url);
};

export const getAllPatientsPageUrl = (id: string) => PathEnum.ALL_PATIENTS.replace(':siteId?', id);

export function isAllSitesSiteId(siteId: string | undefined | null): boolean {
  if (siteId == null || siteId === '') {
    return false;
  }

  let decoded = siteId;
  try {
    decoded = decodeURIComponent(siteId);
  } catch {
    // use raw segment when not percent-encoded
  }

  return decoded === ALL_SITES;
}

export const getCarePathwaysPageUrl = (id: string) => {
  const trimmed = id?.trim();
  if (!trimmed || isAllSitesSiteId(trimmed)) {
    return '/care-pathways';
  }

  return PathEnum.CARE_PATHWAYS.replace(':siteId?', trimmed);
};
export const getPathwayPatientsPageUrl = (siteId: string, carePathwayId: string) => {
  return ['/care-pathway', siteId, carePathwayId].filter(Boolean).join('/');
};
export const getMatchReviewPageUrl = (id: string) => PathEnum.MATCH_REVIEW.replace(':siteId?', id);
export const getTempusNursePageUrl = (id: string) => PathEnum.TEMPUS_NURSE.replace(':siteId?', id);
export const getScreenedPathwaysUrl = (tab: MatchingTab) => {
  return PathEnum.SCREENING_CARE_PATHWAYS_MATCHING.replace(':tab?', kebabCase(tab.toLowerCase()));
};
export const getScreenedPatientsUrl = (tab: MatchingTab) => {
  return PathEnum.TIME_SCREENING_TRIALS_MATCHING.replace(':tab?', kebabCase(tab.toLowerCase()));
};
export const getSiteDocumentsSiteUrl = (id: string | null) => {
  return !id ? '/sites/documents' : PathEnum.SITE_DOCUMENTS.replace(':siteId?', id);
};
/** Site-trial patients page. `siteId` and `tab` are query params so a bookmarked queue restores both. */
export const getSiteTrialPageUrl = (trialId: string, siteId?: string | null, tab?: string | null) => {
  const path = PathEnum.SITE_TRIAL.replace(':trialId', trialId);
  const params = new URLSearchParams();
  if (siteId) {
    params.set('siteId', siteId);
  }
  if (tab) {
    params.set('tab', tab);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/** Legacy next-prescreening patient URL (see {@link PathEnum.PATIENT_CARE_PATHWAY}). */
export const getNextPrescreeningPageUrl = (nextPatientCarePathwayId: string) => {
  return PathEnum.PATIENT_CARE_PATHWAY.replace(':patientCarePathwayId', nextPatientCarePathwayId);
};
/**
 * Legacy query key on {@link PathEnum.PATIENT_V2} for a care-pathway evaluation record id
 * ({@link CarePathwayEvaluationResponseDtoType.id}). Prefer the `:carePathwayEvaluationId` path segment
 * from {@link getTimePrescreeningPageUrl}; still read for old bookmarks.
 *
 * @deprecated Use the path segment instead of this query param for new navigation.
 */
export const PATIENT_WORKSPACE_NEXT_CARE_PATHWAY_ID_QUERY = 'nextPatientCarePathwayId';

/**
 * Tracker list that opened the patient (`PatientTrackerStateId`). Also selects that list's filter slice
 * for criteria groups. Read and written through `patientWorkspaceOrigin.ts`.
 */
export const PATIENT_WORKSPACE_FROM_QUERY = 'from';

/**
 * List that opened the patient when {@link PATIENT_WORKSPACE_FROM_QUERY} is absent.
 * Values: {@link PatientWorkspaceNavCtx}. Read and written through `patientWorkspaceOrigin.ts`.
 */
export const PATIENT_WORKSPACE_NAV_CTX_QUERY = 'navCtx';

/** Values for {@link PATIENT_WORKSPACE_NAV_CTX_QUERY}. */
export enum PatientWorkspaceNavCtx {
  CARE_PATHWAYS = 'care-pathways',
  MATCH_REVIEW = 'match-review',
  SITE_TRIAL = 'site-trial',
}

/** Trial the patient was opened from (site-trial queue or Trial Documents patients tab). */
export const PATIENT_WORKSPACE_NAV_TRIAL_ID_QUERY = 'navTrialId';

/** Site-trial queue tab at the time the patient was opened. Restores the tab on breadcrumb navigation back. */
export const PATIENT_WORKSPACE_NAV_TRIAL_TAB_QUERY = 'navTrialTab';

/**
 * When opening a patient from a pathway queue, preserves the pathway list in workspace breadcrumbs
 * (hub → pathway queue → patient). Paired with {@link PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_NAME_QUERY}.
 */
export const PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY = 'cpQueuePathwayId';

/** Display label for the {@link PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY} queue crumb (URL-encoded). */
export const PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_NAME_QUERY = 'cpQueuePathwayName';

/** Active tab in the care pathway queues tables at the time the patient was opened. Used to restore the tab on breadcrumb navigation back. */
export const PATIENT_WORKSPACE_CP_QUEUE_TAB_QUERY = 'cpQueueTab';

/**
 * Matching sub-tab (kebab case, e.g. `unscreened`) when opening a patient from TIME screening matching/screened tables.
 * Paired with matching-table query keys (`pathwayId`, `stage`, …) for refresh-safe Screen breadcrumbs.
 */
export const PATIENT_WORKSPACE_CP_MATCHING_TAB_QUERY = 'cpMatchTab';

/**
 * Which TIME screening table opened the patient (`matching` vs `screened` list route).
 * Used with {@link PATIENT_WORKSPACE_CP_MATCHING_TAB_QUERY} and matching filter keys for Screen breadcrumbs.
 */
export const PATIENT_WORKSPACE_CP_SCREENING_LIST_QUERY = 'cpScreenList';

export type PatientWorkspaceScreeningListReturn = 'matching' | 'screened';

/**
 * On {@link PathEnum.PATIENT_V2} evaluation tab, selects the trial shown in criteria groups
 * when opening Evaluation from a Site Trials match card.
 */
export const PATIENT_WORKSPACE_CRITERIA_TRIAL_ID_QUERY = 'criteriaTrialId';

/**
 * On {@link PathEnum.PATIENT_V2} evaluation tab, selects trial vs care pathway UI when both are available.
 * Values: `trial`, `carePathway`. Independent of {@link PATIENT_WORKSPACE_NAV_CTX_QUERY} (breadcrumb/queue origin).
 */
export const PATIENT_WORKSPACE_EVALUATION_MODE_QUERY = 'evalMode';

export type TimePrescreeningPageUrlOptions = {
  /** Care-pathway evaluation record id ({@link CarePathwayEvaluationResponseDtoType.id}). */
  carePathwayEvaluationId?: string;
  /**
   * Merged into the query string after the path segment.
   * Origin context (breadcrumbs, top-nav highlighting) comes from `encodePatientWorkspaceOrigin`.
   */
  searchParams?: Record<string, string | undefined>;
};

export const getTimePrescreeningPageUrl = (
  linkageId: string,
  institutionId: string,
  tab: string,
  options?: TimePrescreeningPageUrlOptions | null,
) => {
  if (!linkageId || !institutionId) return '/patient';
  const carePathwayEvaluationId = options?.carePathwayEvaluationId?.trim();
  const base = carePathwayEvaluationId
    ? `/patient/${linkageId}/${institutionId}/${tab}/${carePathwayEvaluationId}`
    : `/patient/${linkageId}/${institutionId}/${tab}`;
  const sp = new URLSearchParams();
  if (options?.searchParams) {
    for (const [key, value] of Object.entries(options.searchParams)) {
      if (value != null && value !== '' && key !== PATIENT_WORKSPACE_NEXT_CARE_PATHWAY_ID_QUERY) {
        sp.set(key, value);
      }
    }
  }
  const q = sp.toString();
  return q ? `${base}?${q}` : base;
};

/** Builder landing tab query param (`/builder?tab=…`). Values: `trial`, `care-pathway`. */
export const BUILDER_TAB_QUERY = 'tab';

export type BuilderLandingTab = 'trial' | 'care-pathway';
export const BUILDER_JOBS_TAB_QUERY = 'jobs';

export type BuilderJobsTab = 'eval-engine' | 'patient-query';

export function getBuilderPageUrl(tab?: BuilderLandingTab): string {
  if (!tab) {
    return PathEnum.BUILDER;
  }
  return `${PathEnum.BUILDER}?${BUILDER_TAB_QUERY}=${tab}`;
}

export function getBuilderTabFromSearch(search: string): BuilderLandingTab | undefined {
  const raw = new URLSearchParams(search).get(BUILDER_TAB_QUERY);
  if (raw === 'trial' || raw === 'care-pathway') {
    return raw;
  }
  return undefined;
}

export function getBuilderJobsTabFromSearch(search: string): BuilderJobsTab | undefined {
  const raw = new URLSearchParams(search).get(BUILDER_JOBS_TAB_QUERY);

  if (raw === 'eval-engine' || raw === 'patient-query') {
    return raw;
  }
  return undefined;
}

/** Evaluation jobs tab from URL search, defaulting to Eval Engine when `jobs` is missing or invalid. */
export function resolveBuilderJobsTab(search: string): BuilderJobsTab {
  return getBuilderJobsTabFromSearch(search) ?? 'eval-engine';
}

/** Location state key for builder nav highlighting when nested routes omit `tab` in the URL. */
export const BUILDER_NAV_TAB_STATE_KEY = 'builderNavTab' as const;

export function getBuilderNavTabFromLocationState(state: unknown): BuilderLandingTab | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const raw = (state as Record<string, unknown>)[BUILDER_NAV_TAB_STATE_KEY];
  if (raw === 'trial' || raw === 'care-pathway') return raw;
  return undefined;
}

/**
 * Landing tab from URL search, then location state, defaulting to Care Pathways when neither is a
 * landing tab (`tab` missing/invalid and no `builderNavTab` state).
 */
export function resolveBuilderLandingTab(search: string, state?: unknown): BuilderLandingTab {
  return getBuilderTabFromSearch(search) ?? getBuilderNavTabFromLocationState(state) ?? 'care-pathway';
}

export function matchesBuilderRoute(pathname: string): boolean {
  return pathname === PathEnum.BUILDER || pathname.startsWith(`${PathEnum.BUILDER}/`);
}

/**
 * Resolves builder landing tab for top-nav highlighting: URL `tab` query, then location state,
 * then defaults to `care-pathway` on builder routes.
 */
export function getBuilderNavTabForNavigation(input: {
  pathname: string;
  search: string;
  state: unknown;
}): BuilderLandingTab | undefined {
  if (!matchesBuilderRoute(input.pathname)) {
    return undefined;
  }
  return resolveBuilderLandingTab(input.search, input.state);
}

/**
 * Appends the landing tab to a builder URL. Nested builder routes have no other signal for whether
 * a project is a trial or a care pathway, so the tab has to survive every hop below `/builder`.
 */
export function withBuilderTab(url: string, tab?: BuilderLandingTab): string {
  return withBuilderQueryParams(url, { [BUILDER_TAB_QUERY]: tab });
}

export function withBuilderJobsTab(url: string, jobsTab?: BuilderJobsTab): string {
  return withBuilderQueryParams(url, { [BUILDER_JOBS_TAB_QUERY]: jobsTab });
}

function withBuilderQueryParams(url: string, params: Record<string, string | undefined>): string {
  const [baseUrl, query = ''] = url.split('?');
  const searchParams = new URLSearchParams(query);

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const serializedParams = searchParams.toString();
  return serializedParams ? `${baseUrl}?${serializedParams}` : baseUrl;
}

export const getBuilderProjectUrl = (projectId: string) => PathEnum.BUILDER_PROJECT.replace(':projectId', projectId);

export function getBuilderProjectPageUrl(projectId: string, tab?: BuilderLandingTab): string {
  return withBuilderTab(getBuilderProjectUrl(projectId), tab);
}
export function getBuilderStagesUrl(projectId: string, tab?: BuilderLandingTab): string {
  return withBuilderTab(PathEnum.BUILDER_PROJECT_STAGES.replace(':projectId', projectId), tab);
}
export const getBuilderRulesetUrl = (projectId: string, rulesetId: string, tab?: BuilderLandingTab) =>
  withBuilderTab(PathEnum.BUILDER_RULESET.replace(':projectId', projectId).replace(':rulesetId', rulesetId), tab);
export const getBuilderEvaluationJobsUrl = (
  projectId: string,
  rulesetId: string,
  tab?: BuilderLandingTab,
  jobsTab?: BuilderJobsTab,
) =>
  withBuilderQueryParams(
    PathEnum.BUILDER_RULESET_EVALUATION_JOBS.replace(':projectId', projectId).replace(':rulesetId', rulesetId),
    { [BUILDER_TAB_QUERY]: tab, [BUILDER_JOBS_TAB_QUERY]: jobsTab },
  );
export const getBuilderEvaluationJobDetailUrl = (
  projectId: string,
  rulesetId: string,
  jobId: string,
  tab?: BuilderLandingTab,
  jobsTab?: BuilderJobsTab,
) =>
  withBuilderQueryParams(
    PathEnum.BUILDER_EVALUATION_JOB_DETAIL.replace(':projectId', projectId)
      .replace(':rulesetId', rulesetId)
      .replace(':jobId', jobId),
    { [BUILDER_TAB_QUERY]: tab, [BUILDER_JOBS_TAB_QUERY]: jobsTab },
  );
export const getBuilderPatientQueryJobDetailUrl = (
  projectId: string,
  rulesetId: string,
  jobId: string,
  tab?: BuilderLandingTab,
  jobsTab?: BuilderJobsTab,
) =>
  withBuilderQueryParams(
    PathEnum.BUILDER_PATIENT_QUERY_JOB_DETAIL.replace(':projectId', projectId)
      .replace(':rulesetId', rulesetId)
      .replace(':jobId', jobId),
    { [BUILDER_TAB_QUERY]: tab, [BUILDER_JOBS_TAB_QUERY]: jobsTab },
  );

export const PROGRAM_MANAGEMENT_TAB_QUERY = 'tab';

export const PROGRAM_MANAGEMENT_SEARCH_QUERY = 'search';

export type ProgramManagementLandingTab = 'sites' | 'pathways';

export type ProgramManagementPathwayDetailTab = 'live-sites' | 'notifications';

export function getProgramManagementSearchFromSearch(search: string): string {
  return new URLSearchParams(search).get(PROGRAM_MANAGEMENT_SEARCH_QUERY) ?? '';
}

export function applyProgramManagementListingSearch(
  prev: URLSearchParams,
  tab: ProgramManagementLandingTab,
  value: string,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.set(PROGRAM_MANAGEMENT_TAB_QUERY, tab);
  if (value.trim()) {
    next.set(PROGRAM_MANAGEMENT_SEARCH_QUERY, value);
  } else {
    next.delete(PROGRAM_MANAGEMENT_SEARCH_QUERY);
  }
  return next;
}

export function applyProgramManagementDetailSearch(prev: URLSearchParams, value: string): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (value.trim()) {
    next.set(PROGRAM_MANAGEMENT_SEARCH_QUERY, value);
  } else {
    next.delete(PROGRAM_MANAGEMENT_SEARCH_QUERY);
  }
  return next;
}

export const PROGRAM_MANAGEMENT_NOTIFICATION_SORT_QUERY = 'sort';

export type ProgramManagementNotificationSort = 'stage' | 'updated';

export function getProgramManagementNotificationSortFromSearch(search: string): ProgramManagementNotificationSort {
  return new URLSearchParams(search).get(PROGRAM_MANAGEMENT_NOTIFICATION_SORT_QUERY) === 'updated'
    ? 'updated'
    : 'stage';
}

export function applyProgramManagementNotificationSort(
  prev: URLSearchParams,
  sort: ProgramManagementNotificationSort,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (sort === 'updated') {
    next.set(PROGRAM_MANAGEMENT_NOTIFICATION_SORT_QUERY, 'updated');
  } else {
    next.delete(PROGRAM_MANAGEMENT_NOTIFICATION_SORT_QUERY);
  }
  return next;
}

export function applyProgramManagementPathwayDetailTab(
  prev: URLSearchParams,
  tab: ProgramManagementPathwayDetailTab,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  next.set(PROGRAM_MANAGEMENT_TAB_QUERY, tab);
  return next;
}

export const getProgramManagementPageUrl = (tab?: ProgramManagementLandingTab) => {
  if (!tab) {
    return PathEnum.PROGRAM_MANAGEMENT;
  }

  return `${PathEnum.PROGRAM_MANAGEMENT}?${PROGRAM_MANAGEMENT_TAB_QUERY}=${tab}`;
};

export const getProgramManagementSiteDetailUrl = (siteId: string): string =>
  PathEnum.PROGRAM_MANAGEMENT_SITE.replace(':siteId', encodeURIComponent(siteId));

export const getProgramManagementPathwayDetailUrl = (pathwayId: string): string =>
  PathEnum.PROGRAM_MANAGEMENT_PATHWAY.replace(':pathwayId', encodeURIComponent(pathwayId));

export const getProgramManagementTabFromSearch = (search: string): ProgramManagementLandingTab | undefined => {
  const raw = new URLSearchParams(search).get(PROGRAM_MANAGEMENT_TAB_QUERY);
  if (raw === 'sites' || raw === 'pathways') {
    return raw;
  }

  return undefined;
};

export const resolveProgramManagementLandingTab = (search: string): ProgramManagementLandingTab => {
  return getProgramManagementTabFromSearch(search) ?? 'sites';
};

export const getProgramManagementPathwayDetailTabFromSearch = (
  search: string,
): ProgramManagementPathwayDetailTab | undefined => {
  const raw = new URLSearchParams(search).get(PROGRAM_MANAGEMENT_TAB_QUERY);
  if (raw === 'live-sites' || raw === 'notifications') {
    return raw;
  }

  return undefined;
};

export const resolveProgramManagementPathwayDetailTab = (search: string): ProgramManagementPathwayDetailTab => {
  return getProgramManagementPathwayDetailTabFromSearch(search) ?? 'live-sites';
};

export const matchesProgramManagementRoute = (pathname: string): boolean => {
  return pathname === PathEnum.PROGRAM_MANAGEMENT || pathname.startsWith(`${PathEnum.PROGRAM_MANAGEMENT}/`);
};

export default PathEnum;
